import { useContext, useEffect, useRef, useState } from "react";
import { DataI } from "../../../../../api/request/Lead/Interfaces/DataInterface.response";
import { PolicyI } from "../../../../../api/request/Lead/Interfaces/PolicyInterface";
import { PricingI } from "../../../../../api/request/Lead/Interfaces/PricingInterface";
import { BeneficiaryI } from "../../../../../api/request/Lead/Interfaces/BeneficiaryInterface";
import { DocumentsI } from "../../../../../api/request/Lead/Interfaces/DocumentsInterface";
import { Promotion } from "../../../../../api/request/Lead/Interfaces/ProductInterface.response";
import { I18nContext } from "../../../../../context/i18n.context";
import { GetlifeContext } from "../../../../../context/GetLifeContext.context";
import ConditionsModalAB from "../../../../../components/ConditionsModal/ConditionsModalAB";
import { BeneficiaryDataI } from "../../../../../api/request/Beneficiaries/Interfaces/BeneficiariesInterfaces";
import HttpPaymentRepository from "../../../../../api/request/Payment/Payment.service";
import { DataCardI } from "../../../../../api/request/Payment/Interfaces/PaymentMethodI.response";
import NeedsCalculator from "../components/NeedsCalculator";
import { getNextFee } from "../../../../../utils/feeCollection";
import { isNativeMode } from "../../../../../native/nativeMode";
import { dashboardPrice } from "../components/pricing/dashboardPrice";
import {
  HeroSection,
  QuickActionsRow,
  PolicySummaryCard,
  BillingCard,
  ProtectionCalculatorCard,
  WinbackCard,
  OtherInsuranceCard,
  QuotesPanel,
} from "../components/globalPosition";
import { PersonProjectI } from "../../../../../api/request/Lead/Interfaces/PersonProjectInterface";
import {
  CapitalChangePreviewI,
  CapitalChangeResultI,
} from "../../../../../api/request/Lead/Interfaces/CapitalChangeInterface";

interface NewGlobalPositionI {
  userData?: DataI;
  policy?: PolicyI;
  pricing?: PricingI;
  beneficiaries?: BeneficiaryI[];
  documents?: DocumentsI | null;
  navigateToRoute: (route: string) => void;
  beneficiaryIT: {
    type: string;
    nominative_beneficiary: number | null;
  };
  domain?: string;
  resumeBeneficiaryIT?: BeneficiaryDataI[];
  policyStatus: "active" | "cancelled";
  /** null = feature off (no se pinta el panel "I tuoi preventivi"). */
  projects?: PersonProjectI[] | null;
  onNewQuote?: () => void;
  /** false = el lead de la sesión es una cotización a medias (no hay póliza que pintar). */
  hasPolicy?: boolean;
  onContinueProject?: (project: PersonProjectI) => Promise<void> | void;
  /** «Archivia preventivo»: solo en los preventivos que el gateway marca archivables. */
  onArchiveProject?: (project: PersonProjectI) => Promise<void> | void;
  /** «Modifica capitale»: solo en los preventivos que el gateway marca capitalChangeable. */
  onPreviewProjectCapital?: (
    project: PersonProjectI,
    capital?: number
  ) => Promise<CapitalChangePreviewI>;
  onChangeProjectCapital?: (
    project: PersonProjectI,
    capital: number,
    operationId: string
  ) => Promise<CapitalChangeResultI>;
}

const NewGlobalPosition = ({
  userData,
  policy,
  pricing,
  beneficiaries,
  documents,
  navigateToRoute,
  beneficiaryIT,
  domain,
  resumeBeneficiaryIT,
  policyStatus,
  projects = null,
  onNewQuote,
  hasPolicy = true,
  onContinueProject,
  onArchiveProject,
  onPreviewProjectCapital,
  onChangeProjectCapital,
}: NewGlobalPositionI) => {
  const {
    state: { translate },
  } = useContext(I18nContext);
  const { leadId, token, handleCallMe } = useContext(GetlifeContext);

  const [dataCard, setDataCard] = useState<DataCardI | null>(null);
  const [showCalculator, setShowCalculator] = useState(false);

  const isCancelled = policyStatus === "cancelled";
  const market = process.env.REACT_APP_DOMAIN_LOCALE;
  const healthWaitlistEnabled =
    (market === "es" || market === "wl_es") && !isNativeMode();
  const lead = userData?.lead;
  const capital = policy?.traits?.find((t) => t.trait === "DEATH")?.capital ?? 0;
  const contractURL = documents?.contractUrl || undefined;

  const nextFee = getNextFee(userData?.feeCollection);
  const frequency =
    nextFee?.frequency ??
    (lead?.purchasePeriod ? lead.purchasePeriod.toUpperCase() : "MONTHLY");
  const nextPaymentDate = nextFee?.expectedPaymentDate;
  // Promo pricing comes from the pricing endpoint: feeCollection.discountAmount
  // is unreliable (often 0 even with an active promotion). Show the promoted
  // price and strike through the regular price when they differ.
  // Sin prima (price null, cohorte SCOR FR) los precios son null: texto neutro.
  const { regularPrice, displayPrice, hasPromo } = dashboardPrice(pricing);

  const couponInfo = pricing?.promotion
    ? {
        conditions: pricing.promotion.conditions,
        exceptions: pricing.promotion.exceptions,
        code: pricing.promotion.promotionalCode ?? undefined,
      }
    : undefined;

  const nodeConditionsModal: any = useRef(null);
  const handleConditions = () => nodeConditionsModal.current?.handleShowPortal();

  useEffect(() => {
    if (!leadId || !token) return;
    const paymentRepository = new HttpPaymentRepository(leadId, token);
    paymentRepository
      .getDataCard()
      .then((res) => setDataCard(res))
      .catch(() => setDataCard(null));
  }, [leadId, token]);

  const paymentMethodLabel = dataCard
    ? `${
        dataCard.type === "sepa_debit"
          ? translate("newDashboard.sepa_debit")
          : translate("newDashboard.card")
      } terminada en **** ${dataCard.lastFourDigits}`
    : "—";

  const quotesPanel =
    null !== projects && onNewQuote && onContinueProject && leadId ? (
      <QuotesPanel
        projects={projects}
        currentLeadId={leadId}
        onContinue={onContinueProject}
        onNewQuote={onNewQuote}
        onArchive={onArchiveProject}
        onPreviewCapital={onPreviewProjectCapital}
        onChangeCapital={onChangeProjectCapital}
      />
    ) : null;

  // Sin póliza: no se pinta una póliza que no existe; solo "I tuoi preventivi".
  if (null !== quotesPanel && !hasPolicy) {
    return <div style={{ width: "100%" }}>{quotesPanel}</div>;
  }

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 900 }}>
        <HeroSection
          lead={lead}
          isCancelled={isCancelled}
          capital={capital}
          pendingCapital={policy?.pendingCapital}
          frequency={frequency}
          nextPaymentDate={nextPaymentDate}
          hasPromo={hasPromo}
          regularPrice={regularPrice}
          displayPrice={displayPrice}
          pricing={pricing}
          onShowConditions={handleConditions}
        />
        <QuickActionsRow
          isCancelled={isCancelled}
          contractURL={contractURL}
          navigateToRoute={navigateToRoute}
          onCall={handleCallMe}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <PolicySummaryCard
            lead={lead}
            policy={policy}
            beneficiaries={beneficiaries}
            resumeBeneficiaryIT={resumeBeneficiaryIT}
            beneficiaryIT={beneficiaryIT}
            domain={domain}
            navigateToRoute={navigateToRoute}
          />
          <BillingCard
            isCancelled={isCancelled}
            paymentMethodLabel={paymentMethodLabel}
            frequency={frequency}
            hasPromo={hasPromo}
            regularPrice={regularPrice}
            displayPrice={displayPrice}
            pricing={pricing}
            navigateToRoute={navigateToRoute}
            onShowConditions={handleConditions}
          />
          {isCancelled ? (
            <WinbackCard onCall={handleCallMe} />
          ) : (
            <ProtectionCalculatorCard onOpen={() => setShowCalculator(true)} />
          )}
          <OtherInsuranceCard
            leadId={leadId}
            token={token}
            waitlistEnabled={healthWaitlistEnabled}
          />
        </div>
        {null !== quotesPanel && <div style={{ width: "100%" }}>{quotesPanel}</div>}
      </div>
      {pricing?.promotion && userData && (
        <ConditionsModalAB
          couponInfo={couponInfo}
          nodeFakeDoorModal={nodeConditionsModal}
          promotion={pricing.promotion as unknown as Promotion}
          collection={userData.feeCollection}
          period={pricing.period ?? "monthly"}
          handleSubmit={() => nodeConditionsModal.current?.handleShowPortal()}
        />
      )}
      {showCalculator && (
        <NeedsCalculator
          currentCapital={capital}
          onClose={() => setShowCalculator(false)}
          onRequestIncrease={() => {
            setShowCalculator(false);
            navigateToRoute("upgrade");
          }}
        />
      )}
    </>
  );
};

export default NewGlobalPosition;
